import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Snowflake, FileCheck2, Beaker, Plus } from "lucide-react";
import { useEnquiry } from "../context/EnquiryContext";
import { scrollToId } from "./Header";
import { toast } from "sonner";

export default function CompoundModal({ product, onClose }) {
    const { add } = useEnquiry();

    useEffect(() => {
        if (!product) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [product, onClose]);

    return (
        <AnimatePresence>
            {product && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-slate-900/50 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="compound-modal-overlay"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 40, opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="compound-modal"
                        className="bg-white w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-slate-200 shadow-2xl"
                    >
                        <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-slate-100 px-7 py-5 flex items-start justify-between gap-4">
                            <div>
                                <div className="font-jbm text-[10px] uppercase tracking-widest text-cyan-700 mb-1.5">{product.category}</div>
                                <h3 className="font-display text-2xl font-semibold tracking-tight text-slate-900">{product.name}</h3>
                                <p className="text-sm text-slate-500 mt-0.5">{product.tagline}</p>
                            </div>
                            <button
                                data-testid="compound-modal-close"
                                onClick={onClose}
                                aria-label="Close specification"
                                className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors duration-300"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-7 py-6">
                            <p className="text-base leading-relaxed text-slate-600">{product.description}</p>

                            {product.uses && product.uses.length > 0 && (
                                <div className="mt-5" data-testid="compound-modal-uses">
                                    <div className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mb-2.5">What it does — studied applications</div>
                                    <ul className="space-y-2">
                                        {product.uses.map((u) => (
                                            <li key={u} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
                                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-1.5 shrink-0" />
                                                {u}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs font-semibold tracking-wide text-amber-800">
                                FOR LABORATORY RESEARCH USE ONLY — NOT FOR HUMAN OR VETERINARY USE
                            </div>

                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    { k: "CAS NUMBER", v: product.cas || "On request" },
                                    { k: "PURITY (HPLC)", v: product.purity },
                                    { k: "PHYSICAL FORM", v: product.form },
                                    { k: "STORAGE", v: product.storage },
                                ].map((row) => (
                                    <div key={row.k} className="border border-slate-100 rounded-xl px-4 py-3 bg-slate-50/60">
                                        <div className="font-jbm text-[10px] uppercase tracking-widest text-slate-400">{row.k}</div>
                                        <div className="font-jbm text-sm text-slate-800 mt-1">{row.v}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-6">
                                <div className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mb-2.5">Available strengths</div>
                                <div className="flex flex-wrap gap-2" data-testid="compound-modal-strengths">
                                    {product.strengths ? (
                                        product.strengths.map((s) => (
                                            <span key={s} className="font-jbm text-xs text-cyan-800 bg-cyan-50 border border-cyan-200 rounded-full px-3.5 py-1.5">
                                                {s}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="font-jbm text-xs text-slate-500 border border-slate-200 rounded-full px-3.5 py-1.5">
                                            Sizes on enquiry
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6">
                                <div className="flex justify-between items-baseline mb-2">
                                    <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400">Representative HPLC trace</span>
                                    <span className="font-jbm text-xs text-cyan-700">{product.purity} main peak</span>
                                </div>
                                <svg viewBox="0 0 400 90" className="w-full h-20 bg-slate-50 rounded-xl border border-slate-100" aria-hidden="true">
                                    <line x1="0" y1="78" x2="400" y2="78" stroke="#CBD5E1" strokeWidth="1" />
                                    <path d="M0 78 C 60 77, 90 76, 130 74 C 150 73, 160 74, 175 78 L 0 78" fill="none" stroke="#94A3B8" strokeWidth="1.5" />
                                    <path d="M170 78 C 195 70, 215 14, 235 12 C 255 14, 275 70, 300 78" fill="rgba(8,145,178,0.12)" stroke="#0891B2" strokeWidth="2" />
                                    <path d="M300 78 C 330 76, 360 77, 400 78" fill="none" stroke="#94A3B8" strokeWidth="1.5" />
                                    <text x="235" y="8" textAnchor="middle" fontSize="9" fill="#0891B2" fontFamily="JetBrains Mono, monospace">RT 6.42 min</text>
                                </svg>
                            </div>

                            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5 text-slate-700" /> COA issued per batch</span>
                                <span className="flex items-center gap-1.5"><Snowflake className="w-3.5 h-3.5 text-slate-700" /> Monitored cold chain</span>
                                <span className="flex items-center gap-1.5"><Beaker className="w-3.5 h-3.5 text-slate-700" /> Third-party mass-spec confirmed</span>
                            </div>

                            <button
                                data-testid="compound-modal-enquiry-button"
                                onClick={() => {
                                    add(product.slug, product.strengths?.[0] || null);
                                    toast.success(`${product.name} added to your enquiry`);
                                    onClose();
                                    setTimeout(() => scrollToId("#enquiry"), 150);
                                }}
                                className="mt-8 w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-medium py-3.5 rounded-full hover:bg-cyan-800 transition-colors duration-300"
                            >
                                <Plus className="w-4 h-4" />
                                Add to research enquiry
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
